import Modal from '@v2/shared/components/Modal';
import Price from '@v2/shared/components/Price';
import Currency from '@v2/shared/components/Currency';
import { cn } from '@v2/shared/lib/utils';
import { useSite } from '@v2/shared/lib/site';
import { PRODUCT_TYPE_MAGAZINE, SaleProduct } from '@v2/shared/lib/megaSale';

import SaleMsrp from './SaleMsrp';
import ProductForm from './ProductForm';
import useProductTile, { PRICE_COLOR } from './useProductTile';

type Props = {
  isOpen: boolean;
  close: () => void;
  product: SaleProduct;
  highestTerm: boolean;
  showCoverPrice: boolean;
  onAdd: (skuCode: string, giftSkuCode: string | null, offerCode: string | null) => Promise<void>;
};

/**
 * The (i) view: everything about a product that does not fit on the tile.
 *
 * The legacy version of this cloned the tile's DOM into a Fancybox and then went round
 * stripping the duplicate `<select>`s it had just created — which is why its term
 * dropdown and the tile's could disagree, and why opening it twice sometimes produced
 * two of everything.
 *
 * This is a dialog with its own state instead, which costs nothing because it renders
 * only while open. The one thing it deliberately shares with the tile is the pricing
 * *logic*, through useProductTile, so both compute a term's price the same way rather
 * than by one copying the other's markup.
 */
export default function ProductDetailDialog({
  isOpen,
  close,
  product,
  highestTerm,
  showCoverPrice,
  onAdd,
}: Props) {
  // Mounted only while open. A sale renders hundreds of tiles, and a dialog per tile
  // that is always in the tree is hundreds of subscriptions and hundreds of portals.
  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} close={close} title={product.name}>
      <DetailContents
        product={product}
        highestTerm={highestTerm}
        showCoverPrice={showCoverPrice}
        onAdd={onAdd}
      />
    </Modal>
  );
}

function DetailContents({
  product,
  highestTerm,
  showCoverPrice,
  onAdd,
}: Omit<Props, 'isOpen' | 'close'>) {
  const site = useSite();
  const tile = useProductTile(product, highestTerm);
  const selected = tile.product;
  const isMagazine = selected.product_type === PRODUCT_TYPE_MAGAZINE;

  return (
    <div className="flex flex-col md:flex-row gap-6 p-4">
      <div className="shrink-0 mx-auto md:mx-0">
        <img
          src={selected.images.normal}
          srcSet={`${selected.images.retina} 2x`}
          width={180}
          height={232}
          alt={selected.name}
          className="w-[180px] h-[232px] object-contain drop-shadow"
        />
      </div>

      <div className="min-w-0 flex-grow">
        <p className="m-0 flex flex-wrap items-baseline gap-x-2">
          <Price value={tile.price} className={cn('text-2xl', PRICE_COLOR)} />
          {isMagazine && tile.perIssue !== null && (
            <span className="text-sm text-primary-500">
              (<Currency value={tile.perIssue} currencyCode={site.currency} />
              /issue)
            </span>
          )}
        </p>
        <p className="m-0 mt-0.5">
          <SaleMsrp
            product={selected}
            price={tile.price}
            pricePercentage={tile.selectedTerm ? parseFloat(String(tile.selectedTerm.percent ?? 0)) || 0 : 0}
            showCoverPrice={showCoverPrice}
          />
        </p>

        {tile.issues !== null && (
          <p className="m-0 mt-2 text-sm text-slate-600">
            {tile.issues} {tile.issues === 1 ? 'issue' : 'issues'}
          </p>
        )}

        <p className="mt-3 text-slate-700">{selected.description}</p>

        {/*
          Language and country, straight under the copy they belong with.
          `jss_product_genome_attributes` holds them; the sale query joins the two rows in
          by name and the JSON hands them over as `attributes`. The legacy page draws the
          same pair, but *after* the Add to Cart button and hidden by CSS everywhere except
          inside the Fancybox — so it reads as a footnote to the form rather than as part
          of the description. Here they sit with the description, which is the only other
          thing on this dialog that describes rather than sells.

          Either one alone still renders. The legacy rule needed both present or it drew
          nothing, which loses a known language over an unknown country for no reason.
        */}
        {(selected.attributes.language || selected.attributes.country) && (
          <ul className="list-none m-0 mt-3 p-0 text-sm text-slate-600">
            {selected.attributes.language && (
              <li>
                <span className="font-semibold">Language:</span> {selected.attributes.language}
              </li>
            )}
            {selected.attributes.country && (
              <li>
                <span className="font-semibold">Country:</span> {selected.attributes.country}
              </li>
            )}
          </ul>
        )}

        {selected.is_digital && selected.digital_instructions && (
          <div className="mt-3 rounded bg-slate-50 p-3 text-sm text-slate-700">
            <p className="m-0 font-semibold mb-1">Digital delivery</p>
            <p className="m-0">{selected.digital_instructions}</p>
          </div>
        )}

        {selected.benefits_instructions && (
          <div className="mt-3 rounded bg-slate-50 p-3 text-sm text-slate-700">{selected.benefits_instructions}</div>
        )}

        <ProductForm
          tile={tile}
          product={selected}
          idSuffix="-detail"
          onAdd={(skuCode, offerCode) => onAdd(skuCode, tile.giftSkuCode, offerCode)}
        />
      </div>
    </div>
  );
}
